import { connect, type DispatchProp } from "react-redux";

import {
	ExclamationCircleFilled,
	LoadingOutlined,
	PlusOutlined,
} from "@ant-design/icons";
import type { Dispatch } from "@reduxjs/toolkit";
import type { FormInstance } from "antd";
import { Button, Form, Spin } from "antd";
import type { AnyObject } from "antd/es/_util/type";
import { actions as routerActions } from "redux-router5";
import type { State as RouterState } from "router5";
import ConnectedAppLayout from "../AppLayout.tsx";
import type { RootState } from "../store.ts";
import * as commonColumns from "../../common/columns.tsx";
import { Card } from "../../common/components/Card.tsx";
import { DrawerItemWrapper } from "../../common/components/DrawerItemWrapper.tsx";
import { ListActionBar } from "../../common/components/ListActionBar.tsx";
import { PageTitleContainer } from "../../common/components/PageTitle.tsx";
import {
	TableWrapper,
	type PaginationAndFilter,
	type TableWrapperProps,
} from "../../common/components/Table.tsx";
import { materialsFilter } from "../common/filter.ts";
import { sanitizedFilter } from "../../common/functions.ts";
import { routeNames } from "../common/routes.ts";
import { materialsActions } from "../../features/materials/materialsSlice.ts";
import type {
	Materials,
	PaginatedMaterialsList,
} from "../generated-axios-client/index.ts";
import { CategorySelectorFormItem } from "../materials/filter-forms/CategorySelectorFormItem.tsx";
import { categoryLabelMap } from "./materialsConstants.ts";

const onFilter =
	(dispatch: Dispatch, form: FormInstance, currentRoute: RouterState) => () => {
		form
			.validateFields()
			.then((values) => {
				dispatch(
					routerActions.navigateTo(
						currentRoute.name,
						sanitizedFilter({ ...currentRoute.params, ...values, page: 1 }),
					),
				);
			})
			.catch((error) => {
				console.log("Form validation error:", error);
			});
	};

const onCancel =
	(dispatch: Dispatch, form: FormInstance, currentRoute: RouterState) => () => {
		dispatch(
			routerActions.navigateTo(
				currentRoute.name,
				sanitizedFilter({
					...currentRoute.params,
					...materialsFilter,
					page: 1,
				}),
			),
		);
		form.resetFields();
	};

const getColumns: TableWrapperProps<
	PaginatedMaterialsList,
	AnyObject
>["getColumns"] = (intl) => [
	commonColumns.simpleColumn("row_number", "table_column_ticket_number", intl),
	commonColumns.simpleColumn("name", "table_column_name", intl),
	commonColumns.simpleColumn("sap_code", "material_batch", intl),
	commonColumns.simpleColumn("category", "material_category", intl),
	commonColumns.simpleColumn("created_at", "table_column_created_at", intl),
	commonColumns.simpleColumn("actions", "table_column_actions", intl),
];

const getTableRows: TableWrapperProps<
	PaginatedMaterialsList,
	AnyObject
>["getRows"] = (intl, dispatch, data, modalApi) => {
	return data.results.map((material: Materials, index: number) => {
		const showDeleteConfirm = () => {
			modalApi?.confirm({
				title: intl.formatMessage({ id: "material_delete_confirmation" }),
				icon: <ExclamationCircleFilled />,
				okText: intl.formatMessage({ id: "confirm" }),
				okType: "danger",
				cancelText: intl.formatMessage({ id: "cancel" }),
				onOk() {
					if (material.id) {
						dispatch(materialsActions.deleteMaterial(material.id));
					}
				},
				onCancel() {},
			});
		};

		const onEdit = () => {
			dispatch(
				routerActions.navigateTo(routeNames.SETTINGS_MATERIAL_EDIT, {
					id: material.id,
				}),
			);
		};

		return {
			key: material.id,
			row_number: index + 1,
			name: material.name,
			sap_code: material.sap_code ?? "—",
			category: categoryLabelMap[material.category] ?? material.category,
			created_at: material.created_at
				? commonColumns.dateValue(material.created_at, intl)
				: null,
			actions: commonColumns.actionsValue(
				undefined,
				undefined,
				undefined,
				undefined,
				undefined,
				onEdit,
				showDeleteConfirm,
				undefined,
				"Material",
			),
		};
	});
};

type MaterialsPageProps = ReturnType<typeof withMaterials> & DispatchProp;

export const MaterialsPage = (props: MaterialsPageProps) => {
	const { results, dispatch, currentRoute, intl, modalApi } = props;

	const [form] = Form.useForm();

	if (!currentRoute) {
		return <Spin indicator={<LoadingOutlined spin />} size="large" />;
	}

	const pagination = results.pagination;

	return (
		<ConnectedAppLayout>
			<PageTitleContainer
				title={intl.formatMessage({ id: "material_table_header_name" })}
				subtitle={intl.formatMessage(
					{ id: "material_table_header_number_of_items" },
					{ no: pagination.total_items },
				)}
				extra={
					<Button
						type="primary"
						icon={<PlusOutlined />}
						onClick={() =>
							dispatch(
								routerActions.navigateTo(routeNames.SETTINGS_MATERIAL_CREATE),
							)
						}
					>
						{intl.formatMessage({ id: "material_create_button" })}
					</Button>
				}
			>
				<Card
					title={
						<ListActionBar
							intl={intl}
							form={form}
							onFilter={onFilter(dispatch, form, currentRoute)}
							onCancel={onCancel(dispatch, form, currentRoute)}
							filterTemplate={materialsFilter}
							currentFilter={currentRoute.params}
							searchBarPlaceholderKey={intl.formatMessage({
								id: "material_table_header_search_placeholder",
							})}
						>
							<DrawerItemWrapper>
								<CategorySelectorFormItem intl={intl} />
							</DrawerItemWrapper>
						</ListActionBar>
					}
				>
					<TableWrapper
						data={results}
						getRows={getTableRows}
						getColumns={getColumns}
						intl={intl}
						modalApi={modalApi}
						route={currentRoute}
						applyFilterAction={(filterParams) =>
							routerActions.navigateTo(
								routeNames.SETTINGS_MATERIAL_LIST,
								filterParams,
							)
						}
						pagination={pagination as PaginationAndFilter}
					/>
				</Card>
			</PageTitleContainer>
		</ConnectedAppLayout>
	);
};

const withMaterials = (state: RootState) => ({
	results: state.materials.list,
	currentRoute: state.router.route,
	intl: state.intl.datetimeIntl,
	modalApi: state.appFeedback.modal,
});

export const ConnectedMaterialsPage = connect(withMaterials)(MaterialsPage);
export default ConnectedMaterialsPage;
